import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import { Box, Typography, Button, Container, Grid, Paper, Chip, Stack, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Section } from '../../components/ui/Section';
import { usePostHog } from '../../components/widgets/PostHogProvider';
import { useEffect, useRef } from 'react';
import { getCaseStudy, getCaseStudyImageUrl } from '../../lib/case-studies';
import { getRelatedPosts } from '../../lib/related';
import { markdownBodyStyles, createMarkdownComponents } from '../../lib/markdown';
import { useSEO, useBreadcrumbSchema } from '../../lib/seo';

export const Route = createFileRoute('/resources/case-studies/$slug')({
  component: CaseStudyDetailPage,
});

function CaseStudyDetailPage() {
  const { slug } = Route.useParams();
  const caseStudy = getCaseStudy(slug);
  const { capture } = usePostHog();
  const hasTrackedView = useRef(false);

  // SEO
  useSEO(caseStudy ? {
    title: caseStudy.title,
    description: caseStudy.excerpt,
    canonical: `/resources/case-studies/${slug}`,
    ogType: 'article',
    ogImage: caseStudy.image,
    article: {
      publishedTime: caseStudy.date,
      author: caseStudy.author,
      tags: caseStudy.tags,
    },
  } : { title: 'Case Study' });

  useBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Case Studies', url: '/resources/case-studies' },
    ...(caseStudy ? [{ name: caseStudy.title, url: `/resources/case-studies/${slug}` }] : []),
  ]);

  // Track case study view
  useEffect(() => {
    if (caseStudy && !hasTrackedView.current) {
      hasTrackedView.current = true;
      capture('case_study_viewed', {
        case_study_slug: slug,
        case_study_title: caseStudy.title,
        case_study_subtitle: caseStudy.subtitle,
      });
    }
  }, [caseStudy, slug, capture]);

  if (!caseStudy) {
    throw notFound();
  }

  const relatedPosts = getRelatedPosts('case-study', slug, caseStudy.tags, caseStudy.related);

  return (
    <>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Button
            component={Link}
            to="/resources/case-studies"
            startIcon={<ArrowBackIcon />}
            sx={{ color: 'white', mb: 3 }}
          >
            Back to Case Studies
          </Button>
          <Typography
            variant="overline"
            sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600, display: 'block' }}
          >
            {caseStudy.subtitle}
          </Typography>
          <Typography variant="h1" sx={{ mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
            {caseStudy.title}
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: 800 }}>
            {caseStudy.excerpt}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 3 }}>
            {caseStudy.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', borderColor: 'rgba(255,255,255,0.3)', borderWidth: 1, borderStyle: 'solid' }}
              />
            ))}
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500, ml: 2 }}>
              {caseStudy.author}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 2 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.7)' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {new Date(caseStudy.date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Section>
        <Grid container spacing={6}>
          <Grid item xs={12} md={8}>
            <Box sx={markdownBodyStyles}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={createMarkdownComponents(getCaseStudyImageUrl)}
              >
                {caseStudy.content}
              </ReactMarkdown>
            </Box>

            {caseStudy.quote && (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  mt: 4,
                  bgcolor: 'primary.50',
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontStyle: 'italic', mb: 2, color: 'text.primary' }}
                >
                  &ldquo;{caseStudy.quote.text}&rdquo;
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {caseStudy.quote.author}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {caseStudy.quote.role}
                </Typography>
              </Paper>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                position: 'sticky',
                top: 100,
              }}
            >
              <Typography variant="h4" sx={{ mb: 2 }}>
                Key Highlights
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {caseStudy.highlights.map((highlight, i) => (
                  <Box component="li" key={i} sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {highlight}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Section>

      {/* Related posts */}
      {relatedPosts.length > 0 && (
        <Container maxWidth="lg" sx={{ pb: 6 }}>
          <Divider sx={{ mb: 6 }} />
          <Typography variant="h4" sx={{ mb: 3 }}>
            Related Reading
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            {relatedPosts.map((related) => (
              <Paper
                key={`${related.type}:${related.slug}`}
                component={Link}
                to={related.url}
                elevation={0}
                onClick={() => {
                  capture('related_article_clicked', {
                    source_post_slug: slug,
                    source_post_title: caseStudy.title,
                    related_post_slug: related.slug,
                    related_post_title: related.title,
                  });
                }}
                sx={{
                  flex: 1,
                  p: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                <Typography variant="h5" sx={{ mb: 1, color: 'text.primary' }}>
                  {related.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {related.excerpt}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Container>
      )}
    </>
  );
}
